import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import type { Consultant } from '../../types';
import { storage, db } from '../../firebase';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { doc, getDoc, addDoc, updateDoc, collection } from 'firebase/firestore';
import { useToast } from '../../hooks/useToast';


interface AdminConsultantFormPageProps {}

const UploadIcon: React.FC<{className?: string}> = ({className}) => <svg className={className} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="17 8 12 3 7 8"></polyline><line x1="12" y1="3" x2="12" y2="15"></line></svg>;

const ImageUploader: React.FC<{ label: string; value: string; onChange: (value: string) => void; }> = ({ label, value, onChange }) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const [isUploading, setIsUploading] = useState(false);
    const { showToast } = useToast();

    const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            setIsUploading(true);
            try {
                const storageRef = ref(storage, `consultants/${file.name}_${Date.now()}`);
                await uploadBytes(storageRef, file);
                const downloadURL = await getDownloadURL(storageRef);
                onChange(downloadURL);
            } catch (error: any) {
                console.error("Upload failed:", error);
                let message = "Upload failed. Please try again.";
                if (error.code === 'storage/unauthorized') {
                    message = 'Permission Denied: Please check your Firebase Storage rules to allow write access.';
                }
                showToast(message, { type: 'error' });
            } finally {
                setIsUploading(false);
            }
        }
    };
    
    return (
        <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
            <div 
                className="relative w-48 h-48 mx-auto border-2 border-dashed border-gray-300 rounded-md flex items-center justify-center cursor-pointer hover:border-gray-500 transition-colors bg-gray-50"
                onClick={() => !isUploading && inputRef.current?.click()}
            >
                 {isUploading && (
                    <div className="absolute inset-0 bg-white/80 flex items-center justify-center rounded-md z-10">
                        <div className="w-8 h-8 border-2 border-dashed rounded-full animate-spin border-gray-800"></div>
                    </div>
                )}
                {value ? (
                    <img src={value} alt={label} className="h-full w-full object-cover rounded-md" />
                ) : (
                    <div className="text-center text-gray-500">
                        <UploadIcon className="mx-auto h-8 w-8"/>
                        <span className="mt-2 text-sm">Click to upload</span>
                    </div>
                )}
            </div>
            <input 
                type="file"
                ref={inputRef}
                className="hidden"
                accept="image/png, image/jpeg, image/gif, image/webp"
                onChange={handleFileChange}
                disabled={isUploading}
            />
        </div>
    );
};


const AdminConsultantFormPage: React.FC<AdminConsultantFormPageProps> = () => {
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    const isEditing = Boolean(id);
    const { showToast } = useToast();
    const [isLoading, setIsLoading] = useState(isEditing);
    const [isSaving, setIsSaving] = useState(false);
    
    const [consultant, setConsultant] = useState<Omit<Consultant, 'id'>>({
        name: '',
        role: '',
        company: '',
        avatarUrl: '',
    });

    useEffect(() => {
        if (isEditing && id) {
            const fetchConsultant = async () => {
                const docRef = doc(db, 'consultants', id);
                const docSnap = await getDoc(docRef);
                if (docSnap.exists()) {
                    setConsultant(docSnap.data() as Consultant);
                } else {
                    showToast('Consultant not found.', { type: 'error' });
                    navigate('/admin/consultants');
                }
                setIsLoading(false);
            }
            fetchConsultant();
        } else {
            setIsLoading(false);
        }
    }, [id, isEditing, navigate, showToast]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setConsultant(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!consultant.name.trim()) {
            showToast('Consultant name is required.', { type: 'error' });
            return;
        }
        setIsSaving(true);
        try {
            if (isEditing && id) {
                const memberRef = doc(db, 'consultants', id);
                await updateDoc(memberRef, consultant);
                showToast('Consultant updated successfully!', { type: 'success' });
            } else {
                await addDoc(collection(db, 'consultants'), consultant);
                showToast('Consultant added successfully!', { type: 'success' });
            }
            navigate('/admin/consultants');
        } catch (error) {
            console.error("Error saving consultant:", error);
            showToast('Failed to save consultant. Please check the console.', { type: 'error' });
            setIsSaving(false);
        }
    };
    
    if (isLoading) {
        return <div className="text-center p-8">Loading consultant details...</div>;
    }

    return (
        <div>
            <h2 className="text-3xl font-bold text-gray-800 mb-6">{isEditing ? 'Edit Consultant' : 'Add New Consultant'}</h2>
            <form onSubmit={handleSubmit} className="space-y-8">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    <div className="lg:col-span-2 space-y-6 bg-white p-8 rounded-lg shadow-md h-fit">
                        <div>
                            <label htmlFor="name" className="block text-sm font-medium text-gray-700">Full Name</label>
                            <input type="text" name="name" id="name" value={consultant.name} onChange={handleChange} className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-gray-500 focus:border-gray-500"/>
                        </div>
                        <div>
                            <label htmlFor="role" className="block text-sm font-medium text-gray-700">Role / Position</label>
                            <input type="text" name="role" id="role" value={consultant.role} onChange={handleChange} className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-gray-500 focus:border-gray-500"/>
                        </div>
                        <div>
                            <label htmlFor="company" className="block text-sm font-medium text-gray-700">Company</label>
                            <input type="text" name="company" id="company" value={consultant.company} onChange={handleChange} className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-gray-500 focus:border-gray-500"/>
                        </div>
                    </div>
                    <div className="space-y-6 bg-white p-8 rounded-lg shadow-md h-fit">
                        <ImageUploader label="Avatar" value={consultant.avatarUrl} onChange={(val) => setConsultant(p => ({...p, avatarUrl: val}))}/>
                    </div>
                </div>

                <div className="flex justify-end space-x-4 bg-white p-4 rounded-lg shadow-md sticky bottom-4">
                    <button type="button" onClick={() => navigate('/admin/consultants')} className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300">
                        Cancel
                    </button>
                    <button type="submit" disabled={isSaving} className="px-4 py-2 bg-gray-800 text-white rounded-md hover:bg-gray-700 disabled:bg-gray-400">
                        {isSaving ? 'Saving...' : 'Save Consultant'}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default AdminConsultantFormPage;